import { useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router";

const MIN_PASSWORD_LENGTH = 12;

const inputClass =
  "w-full rounded-lg border border-ink/10 bg-paper px-4 py-3 text-[14.5px] text-ink transition placeholder:text-ink-4/60 focus:border-tide focus:outline-none focus:ring-[3px] focus:ring-tide/15";

const labelClass =
  "mb-2 block text-[11.5px] font-semibold uppercase tracking-wider text-ink-4";

const buttonClass =
  "mt-2 flex w-full items-center justify-center gap-2 rounded-lg bg-tide px-3 py-3 text-[14.5px] font-medium text-paper-hi transition hover:-translate-y-px hover:bg-tide-d hover:shadow-[0_10px_30px_-8px_rgba(38,99,199,0.35)] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0 disabled:hover:bg-tide disabled:hover:shadow-none";

function ResetShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative min-h-screen overflow-hidden bg-paper text-ink antialiased pt-[var(--banner-h)]">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-[140px] -top-[200px] h-[520px] w-[520px] rounded-full bg-tide/10 blur-[80px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-[240px] -right-[120px] h-[460px] w-[460px] rounded-full bg-spark/10 blur-[80px]"
      />

      <nav className="relative z-20 flex items-center justify-between px-8 pt-7 md:px-14">
        <div className="flex items-center gap-2.5">
          <img
            src="/reflex-mark.svg"
            alt=""
            width="32"
            height="32"
            className="h-8 w-8"
          />
          <span className="text-[15px] font-semibold tracking-tight">
            Reflex Labs
          </span>
        </div>
        <a
          href="mailto:hello@reflexlabs.io"
          className="text-[13px] text-ink-4 transition hover:text-ink"
        >
          Need help?
        </a>
      </nav>

      <main className="relative z-10 flex min-h-[calc(100vh-140px)] items-center justify-center px-6 py-12">
        <div className="portal-reveal portal-reveal-1 w-full max-w-[400px]">
          {children}
        </div>
      </main>

      <footer className="relative z-10 flex items-center justify-between px-8 py-6 text-[11.5px] text-ink-4 md:px-14">
        <span>© {new Date().getFullYear()} Reflex Labs</span>
        <Link to="/login" className="hover:text-ink-3">
          Back to sign in
        </Link>
      </footer>
    </div>
  );
}

function RequestResetForm() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch("/auth/password/reset-request", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setError(data.error || "Something went wrong. Please try again.");
        setSubmitting(false);
        return;
      }
      setSubmitting(false);
      setSent(true);
    } catch {
      setError("Could not reach the server. Please try again.");
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <>
        <div className="mb-7 text-center">
          <h1 className="font-display text-[36px] font-medium leading-tight tracking-tight">
            Check <em className="font-normal italic text-tide">your email</em>.
          </h1>
        </div>
        <div className="login-card-shadow rounded-2xl bg-paper-hi p-8 text-center">
          <p className="text-[14.5px] leading-relaxed text-ink-3">
            If <strong className="text-ink">{email}</strong> has an account, a
            reset link is on its way. The link expires in 60 minutes and can be
            used once.
          </p>
          <p className="mt-4 text-[13px] text-ink-4">
            No email after a few minutes? Check your spam folder, or{" "}
            <button
              type="button"
              onClick={() => setSent(false)}
              className="font-medium text-tide hover:underline"
            >
              try again
            </button>
            .
          </p>
        </div>
        <div className="mt-5 text-center text-[12.5px] text-ink-4">
          <Link to="/login" className="font-medium text-tide hover:underline">
            Back to sign in
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-7 text-center">
        <h1 className="font-display text-[36px] font-medium leading-tight tracking-tight">
          Reset <em className="font-normal italic text-tide">your password</em>.
        </h1>
      </div>

      <div className="login-card-shadow rounded-2xl bg-paper-hi p-8">
        <p className="mb-5 text-[13.5px] leading-relaxed text-ink-3">
          Enter the email address you sign in with and we&rsquo;ll send you a
          link to choose a new password. The link expires in 60 minutes and can
          be used once.
        </p>
        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <div>
            <label htmlFor="reset-email" className={labelClass}>
              Email
            </label>
            <input
              id="reset-email"
              type="email"
              autoComplete="email"
              autoFocus
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={inputClass}
            />
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-rust/30 bg-rust-s/40 px-3 py-2 text-[13px] text-rust"
            >
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !email}
            className={buttonClass}
          >
            {submitting ? "Sending…" : "Send reset link"}
          </button>
        </form>
      </div>

      <div className="mt-5 text-center text-[12.5px] text-ink-4">
        Remembered it?{" "}
        <Link to="/login" className="font-medium text-tide hover:underline">
          Back to sign in
        </Link>
        .
      </div>
    </>
  );
}

function CompleteResetForm({ token }: { token: string }) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // True when the server said the token itself is dead (invalid, expired,
  // or already used) — keyed off the response status, not the error copy.
  const [linkDead, setLinkDead] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (submitting) return;
    setLinkDead(false);
    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError("Passwords don't match.");
      return;
    }
    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch("/auth/password/reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = (await response.json().catch(() => ({}))) as {
        error?: string;
        code?: string;
      };
      if (!response.ok) {
        setError(data.error || "Something went wrong. Please try again.");
        // The API sends a machine-readable `code` — never sniff the
        // human-facing copy.
        setLinkDead(
          ["invalid_token", "expired_token", "used_token"].includes(
            data.code ?? "",
          ),
        );
        setSubmitting(false);
        return;
      }
      setDone(true);
    } catch {
      setError("Could not reach the server. Please try again.");
      setLinkDead(false);
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <>
        <div className="mb-7 text-center">
          <h1 className="font-display text-[36px] font-medium leading-tight tracking-tight">
            Password <em className="font-normal italic text-tide">updated</em>.
          </h1>
        </div>
        <div className="login-card-shadow rounded-2xl bg-paper-hi p-8 text-center">
          <p className="text-[14.5px] leading-relaxed text-ink-3">
            Your password has been changed and any previous sessions have been
            signed out. Sign in with your new password to continue.
          </p>
          <Link to="/login" className={buttonClass + " mt-6"}>
            Go to sign in
          </Link>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-7 text-center">
        <h1 className="font-display text-[36px] font-medium leading-tight tracking-tight">
          Choose a{" "}
          <em className="font-normal italic text-tide">new password</em>.
        </h1>
      </div>

      <div className="login-card-shadow rounded-2xl bg-paper-hi p-8">
        <p className="mb-5 text-[13.5px] leading-relaxed text-ink-3">
          Pick a new password of at least {MIN_PASSWORD_LENGTH} characters.
          Once it&rsquo;s saved you&rsquo;ll be signed out everywhere and can
          sign back in with the new password.
        </p>
        <form className="space-y-4" onSubmit={handleSubmit} noValidate>
          <div>
            <label htmlFor="reset-new-password" className={labelClass}>
              New password
            </label>
            <div className="relative">
              <input
                id="reset-new-password"
                type={showPassword ? "text" : "password"}
                autoComplete="new-password"
                autoFocus
                required
                minLength={MIN_PASSWORD_LENGTH}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                className={inputClass + " pr-10"}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-4 transition hover:text-ink"
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="reset-confirm-password" className={labelClass}>
              Confirm password
            </label>
            <input
              id="reset-confirm-password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              minLength={MIN_PASSWORD_LENGTH}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="••••••••••••"
              className={inputClass}
            />
          </div>

          {error && (
            <div
              role="alert"
              className="rounded-lg border border-rust/30 bg-rust-s/40 px-3 py-2 text-[13px] text-rust"
            >
              {error}
              {linkDead && (
                <>
                  {" "}
                  <Link
                    to="/reset-password"
                    className="font-medium text-tide hover:underline"
                  >
                    Request a new link
                  </Link>
                  .
                </>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting || !password || !confirm}
            className={buttonClass}
          >
            {submitting ? "Saving…" : "Save new password"}
          </button>
        </form>
      </div>

      <div className="mt-5 text-center text-[12.5px] text-ink-4">
        <Link to="/login" className="font-medium text-tide hover:underline">
          Back to sign in
        </Link>
      </div>
    </>
  );
}

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  return (
    <ResetShell>
      {token ? <CompleteResetForm token={token} /> : <RequestResetForm />}
    </ResetShell>
  );
}
